import React, { useState, useCallback, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import Home from "../pages/Home";
import BuildingBusiness from "../pages/BuildingBusiness";
import BusinessType from "../pages/BusinessType";
import TagSelect from "../pages/TagSelect";
import CreateService from "../pages/CreateService";
import Availability from "../pages/Availability";
import BusinessDesign from "../pages/BusinessDesign";
import Success from "../pages/Success";
import OnboardingChat from "../pages/OnboardingChat";
import OnboardingLayout from "../components/OnboardingLayout/OnboardingLayout";

const STEPS = [
  { key: "register",          stepGroup: 0 },  // 0 — no bars
  { key: "business-type",     stepGroup: 1 },  // 1 — bar 1
  { key: "tag-select",        stepGroup: 1 },  // 1 — bar 1
  { key: "create-service",    stepGroup: 2 },  // 2 — bar 1+2
  { key: "availability",      stepGroup: 2 },  // 2 — bar 1+2
  { key: "business-design",   stepGroup: 2 },  // 2 — bar 1+2
  { key: "building-business", stepGroup: 3 },  // 3 — transition animation (before success)
  { key: "success",           stepGroup: 3 },  // 3 — all bars
];

const OnboardingFlow = () => {
  const navigate = useNavigate();
  // Persist step in sessionStorage so hot-reload / re-mount doesn't lose it
  // Skip building-business on reload — it's a one-time transition animation
  const [step, _setStep] = useState(() => {
    const saved = sessionStorage.getItem("onboarding_step");
    const idx = saved ? parseInt(saved, 10) : 0;
    if (STEPS[idx]?.key === "building-business") {
      const next = idx + 1;
      sessionStorage.setItem("onboarding_step", String(next));
      return next;
    }
    return idx;
  });
  const [stepData, setStepData] = useState(() => {
    try {
      const saved = sessionStorage.getItem("onboarding_stepData");
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  const setStep = useCallback((valOrFn) => {
    _setStep((prev) => {
      const next = typeof valOrFn === "function" ? valOrFn(prev) : valOrFn;
      sessionStorage.setItem("onboarding_step", String(next));
      return next;
    });
  }, []);

  const goNext = useCallback((data) => {
    if (data) {
      setStepData((prev) => {
        const next = { ...prev, ...data };
        sessionStorage.setItem("onboarding_stepData", JSON.stringify(next));
        return next;
      });
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo(0, 0);
  }, []);

  const goBack = useCallback(() => {
    setStep((s) => {
      // After the registration step, the user already has an auth_token
      // (returned from /business/login or /business/register). Going
      // back into "register" would just re-render the empty form even
      // though they're already authenticated — confusing and lets them
      // resubmit. Clamp the floor at step 1 once a token exists.
      const hasToken = !!localStorage.getItem("auth_token");
      const floor = hasToken ? 1 : 0;
      return Math.max(s - 1, floor);
    });
    window.scrollTo(0, 0);
  }, []);

  const goToStep = useCallback((stepName, data) => {
    const idx = STEPS.findIndex((s) => s.key === stepName);
    if (idx >= 0) {
      if (data) {
        setStepData((prev) => {
          const next = { ...prev, ...data };
          sessionStorage.setItem("onboarding_stepData", JSON.stringify(next));
          return next;
        });
      }
      setStep(idx);
      window.scrollTo(0, 0);
    }
  }, []);

  // Signup A/B test: the "chat" group sets the business up in a chat
  // instead of the business-type → design steps
  const [variant, setVariant] = useState(() => sessionStorage.getItem("signup_variant") || "form");
  useEffect(() => {
    setVariant(sessionStorage.getItem("signup_variant") || "form");
  }, [step]);
  const switchToForm = useCallback(() => {
    sessionStorage.setItem("signup_variant", "form");
    setVariant("form");
  }, []);

  const current = STEPS[step];
  const isFirstStep = step === 0;
  const isChat = variant === "chat" && current.key === "business-type";
  const isBuildingStep = current.key === "building-business";
  const isSuccess = current.key === "success";
  const hideChrome = isFirstStep || isBuildingStep || isSuccess || isChat;

  // When the state machine reaches "success", push the URL to
  // /register/success so Google Ads / Analytics can fire a conversion
  // pixel on the URL change. The standalone /register/success route
  // (defined below) renders <Success /> for the user, so they still
  // see the celebration page — just with a real, trackable URL.
  useEffect(() => {
    if (isSuccess) {
      // Replace, not push — back-button shouldn't take the user back
      // into the now-completed onboarding flow.
      navigate("/register/success", { replace: true });
      // Tell GTM we reached the success page. React Router doesn't
      // trigger a real page reload in this SPA, so a plain Page View
      // trigger in GTM never fires — we push a custom event that the
      // Google Ads conversion tag listens to. Trigger setup in GTM:
      //   Trigger Type: Custom Event
      //   Event name:   registration_success
      // The conversion event itself is sent by <Success /> (once per
      // business) — pushing it here too counted every signup 3 times.
    }
  }, [isSuccess, navigate]);

  const renderStep = () => {
    switch (current.key) {
      case "register":
        return <Home onNext={goNext} />;
      case "building-business":
        return <BuildingBusiness onNext={goNext} />;
      case "business-type":
        if (isChat) {
          return (
            <OnboardingChat
              onDone={() => goToStep("building-business")}
              onSwitchToForm={switchToForm}
            />
          );
        }
        return <BusinessType onNext={goNext} onBack={goBack} />;
      case "tag-select":
        return <TagSelect onNext={goNext} onBack={goBack} stepData={stepData} />;
      case "create-service":
        return <CreateService onNext={goNext} onBack={goBack} goToStep={goToStep} />;
      case "availability":
        return <Availability onNext={goNext} onBack={goBack} />;
      case "business-design":
        return <BusinessDesign onNext={goNext} onBack={goBack} />;
      case "success":
        return <Success />;
      default:
        return <Home onNext={goNext} />;
    }
  };

  return (
    <OnboardingLayout
      // The chat has its own header; the layout's name bar would double it
      stepGroup={isChat ? 0 : current.stepGroup}
      onBack={!hideChrome ? goBack : null}
      showSteps={!hideChrome}
    >
      {renderStep()}
    </OnboardingLayout>
  );
};

const AppRoutes = () => {
  return (
    <Router>
      <Routes>
        <Route exact path="/" element={<Navigate to="/register" replace />} />
        <Route exact path="/register" element={<OnboardingFlow />} />
        <Route exact path="/business-type" element={<Navigate to="/register" replace />} />
        <Route exact path="/tag-select" element={<Navigate to="/register" replace />} />
        <Route exact path="/services" element={<Navigate to="/register" replace />} />
        <Route exact path="/create-service" element={<Navigate to="/register" replace />} />
        <Route exact path="/availability" element={<Navigate to="/register" replace />} />
        <Route exact path="/business-design" element={<Navigate to="/register" replace />} />
        <Route exact path="/update-profile" element={<Navigate to="/register" replace />} />
        {/* Real success page — also the Google Ads conversion target.
            Lands here either via OnboardingFlow's effect above or via
            updateProfileCard's explicit navigate(). Direct visits also
            render correctly because <Success /> reads businessCode /
            ownerName / authToken from localStorage. */}
        <Route exact path="/register/success" element={<Success />} />
      </Routes>
    </Router>
  );
};

export default AppRoutes;
